package org.example.zone360.utils.mediaManagerUtils
import kotlinx.browser.document
import org.example.zone360.utils.Config
import org.w3c.dom.HTMLVideoElement

object VideoManager {

    private const val MAX_CACHED = Config.MAX_CACHED

    data class VideoEntry(
        val url: String,
        var lastTime: Double = 0.0
    )

    private val cache = mutableMapOf<String, VideoEntry>()
    private val fullyDownloaded = LinkedHashSet<String>()
    private val accessOrder = ArrayDeque<String>()  // LRU tracking
    private var activeUrl: String? = null

    fun prepare(url: String, elementId: String): HTMLVideoElement? {
        val current = activeUrl
        if (current != null && current != url) {
            suspendByUrl(current)
        }

        activeUrl = url

        // Update LRU order
        accessOrder.remove(url)
        accessOrder.addLast(url)

        // Evict oldest if over limit
        while (accessOrder.size > MAX_CACHED) {
            val evicted = accessOrder.removeFirst()
            cache.remove(evicted)
            fullyDownloaded.remove(evicted)
        }

        val element = document.getElementById(elementId) as? HTMLVideoElement ?: return null

        val entry = cache[url]
        if (entry != null) {
            if (element.src.isEmpty() || !element.src.contains(url.substringAfterLast("/"))) {
                element.src = url
                element.load()
            }
            if (entry.lastTime > 0.0 && url !in fullyDownloaded) {
                element.currentTime = entry.lastTime
            }
            return element
        }

        val newEntry = VideoEntry(url = url)
        cache[url] = newEntry
        element.src = url
        element.load()
        element.addEventListener("canplaythrough", {
            fullyDownloaded.add(url)
        })

        return element
    }

    fun suspendDownload(url: String, elementId: String) {
        val element = document.getElementById(elementId) as? HTMLVideoElement
        val entry = cache[url] ?: return

        element?.let {
            it.pause()
            if (it.currentTime > 0.0) entry.lastTime = it.currentTime
            if (url !in fullyDownloaded) {
                it.src = ""
                it.load()
            }
        }

        if (activeUrl == url) activeUrl = null
    }

    fun release(url: String, elementId: String) {
        val element = document.getElementById(elementId) as? HTMLVideoElement
        element?.pause()
        element?.src = ""
        element?.load()
        cache.remove(url)
        fullyDownloaded.remove(url)
        accessOrder.remove(url)
        if (activeUrl == url) activeUrl = null
    }

    fun releaseAll() {
        cache.forEach { (url, _) -> suspendByUrl(url) }
        cache.clear()
        accessOrder.clear()
//        fullyDownloaded intentionally NOT cleared
        activeUrl = null
    }

    fun isReady(url: String): Boolean = url in fullyDownloaded
    fun isCached(url: String): Boolean = cache.containsKey(url)
    fun getSavedTime(url: String): Double = cache[url]?.lastTime ?: 0.0

    private fun suspendByUrl(url: String) {
        val entry = cache[url] ?: return
        val videos = document.querySelectorAll("video")
        for (i in 0 until videos.length) {
            val el = videos.item(i) as? HTMLVideoElement ?: continue
            if (el.src.contains(url.substringAfterLast("/")) || el.src == url) {
                el.pause()
                if (el.currentTime > 0.0) entry.lastTime = el.currentTime
                if (url !in fullyDownloaded) {
                    el.src = ""
                    el.load()
                }
                break
            }
        }
        if (activeUrl == url) activeUrl = null
    }
}